import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect } from 'react';
import { X, Search, Loader } from 'lucide-react';
import { supabase } from '../../lib/supabase';
/**
 * AddItemModal Component
 *
 * Modal to add items to queue from unified sources:
 * - Steps (with duration tracking)
 * - Breaths (with time estimation)
 * - Vision Lists (one-time projects from task_lists)
 * - Habit Groups (recurring routines)
 * - Time Blocks (focus durations)
 */
export function AddItemModal({ isOpen, onClose, onAdd }) {
    const [activeTab, setActiveTab] = useState('step');
    const [searchQuery, setSearchQuery] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isAdding, setIsAdding] = useState(null);
    // Data for each tab
    const [steps, setSteps] = useState([]);
    const [breaths, setBreaths] = useState([]);
    const [visionLists, setVisionLists] = useState([]);
    const [habitGroups, setHabitGroups] = useState([]);
    const [timeBlocks, setTimeBlocks] = useState([]);
    // Fetch data when modal opens or tab changes
    useEffect(() => {
        if (!isOpen)
            return;
        const fetchItems = async () => {
            try {
                setIsLoading(true);
                const { data: { user } } = await supabase.auth.getUser();
                if (!user)
                    return;
                switch (activeTab) {
                    case 'step': {
                        const { data } = await supabase
                            .from('steps')
                            .select('id, label, duration, color, icon')
                            .eq('user_id', user.id)
                            .eq('completed', false)
                            .order('created_at', { ascending: false });
                        setSteps(data || []);
                        break;
                    }
                    case 'breath': {
                        const { data } = await supabase
                            .from('breaths')
                            .select('id, name, time_estimation_seconds')
                            .eq('user_id', user.id)
                            .order('created_at', { ascending: false });
                        setBreaths(data || []);
                        break;
                    }
                    case 'vision_list': {
                        const { data } = await supabase
                            .from('lengths')
                            .select('id, name, duration, type, frequency')
                            .eq('user_id', user.id)
                            .eq('type', 'vision_list')
                            .order('created_at', { ascending: false });
                        setVisionLists(data || []);
                        break;
                    }
                    case 'habit_group': {
                        const { data } = await supabase
                            .from('lengths')
                            .select('id, name, duration, type, frequency')
                            .eq('user_id', user.id)
                            .eq('type', 'habit_group')
                            .order('created_at', { ascending: false });
                        setHabitGroups(data || []);
                        break;
                    }
                    case 'time_block': {
                        const { data } = await supabase
                            .from('lengths')
                            .select('id, name, duration, type, frequency')
                            .eq('user_id', user.id)
                            .eq('type', 'time_block')
                            .order('created_at', { ascending: false });
                        setTimeBlocks(data || []);
                        break;
                    }
                }
            }
            finally {
                setIsLoading(false);
            }
        };
        fetchItems();
    }, [isOpen, activeTab]);
    const handleAddItem = async (itemId) => {
        setIsAdding(itemId);
        try {
            // Map tab names to proper ItemType
            const itemType = activeTab === 'step' ? 'step' :
                activeTab === 'breath' ? 'breath' :
                    'length'; // vision_list, habit_group, time_block all use 'length' type
            await onAdd(itemType, itemId);
            setSearchQuery('');
        }
        finally {
            setIsAdding(null);
        }
    };
    if (!isOpen)
        return null;
    // Filter items based on search
    let items = [];
    let getItemName = () => '';
    let getItemDuration = () => '';
    let getItemSubtitle = () => '';
    switch (activeTab) {
        case 'step':
            items = steps;
            getItemName = (item) => item.label;
            getItemDuration = (item) => `${Math.floor(item.duration / 60)}:${String(item.duration % 60).padStart(2, '0')}`;
            break;
        case 'breath':
            items = breaths;
            getItemName = (item) => item.name;
            getItemDuration = (item) => `${Math.floor(item.time_estimation_seconds / 60)}:${String(item.time_estimation_seconds % 60).padStart(2, '0')}`;
            break;
        case 'vision_list':
            items = visionLists;
            getItemName = (item) => item.name;
            getItemDuration = (item) => item.duration ? `${Math.floor(item.duration / 60)}:${String(item.duration % 60).padStart(2, '0')}` : '—';
            break;
        case 'habit_group':
            items = habitGroups;
            getItemName = (item) => item.name;
            getItemDuration = (item) => item.duration ? `${Math.floor(item.duration / 60)}:${String(item.duration % 60).padStart(2, '0')}` : '—';
            getItemSubtitle = (item) => item.frequency ? `${item.frequency}` : '';
            break;
        case 'time_block':
            items = timeBlocks;
            getItemName = (item) => item.name;
            getItemDuration = (item) => item.duration ? `${Math.floor(item.duration / 60)}:${String(item.duration % 60).padStart(2, '0')}` : '—';
            break;
    }
    const filteredItems = items.filter(item => getItemName(item).toLowerCase().includes(searchQuery.toLowerCase()));
    return (_jsx("div", { className: "fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50", children: _jsxs("div", { className: "bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col", children: [_jsxs("div", { className: "px-6 py-4 border-b border-slate-200 flex items-center justify-between", children: [_jsx("h2", { className: "text-lg font-bold text-slate-900", children: "Add to Queue" }), _jsx("button", { onClick: onClose, className: "p-1 hover:bg-slate-100 rounded transition-colors", children: _jsx(X, { className: "w-5 h-5 text-slate-600" }) })] }), _jsx("div", { className: "flex border-b border-slate-200 bg-slate-50 px-6 overflow-x-auto min-h-[3rem] shrink-0", children: ['step', 'breath', 'vision_list', 'habit_group', 'time_block'].map(tab => {
                        const labels = {
                            'step': 'Steps',
                            'breath': 'Breaths',
                            'vision_list': 'Vision Lists',
                            'habit_group': 'Habit Groups',
                            'time_block': 'Time Blocks'
                        };
                        return (_jsx("button", { onClick: () => setActiveTab(tab), className: `px-4 py-3 font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === tab
                                ? 'border-blue-500 text-blue-600 bg-white'
                                : 'border-transparent text-slate-600 hover:text-slate-900'}`, children: labels[tab] }, tab));
                    }) }), _jsx("div", { className: "px-6 py-3 border-b border-slate-200", children: _jsxs("div", { className: "relative", children: [_jsx(Search, { className: "absolute left-3 top-3 w-4 h-4 text-slate-400" }), _jsx("input", { type: "text", placeholder: "Search...", value: searchQuery, onChange: (e) => setSearchQuery(e.target.value), className: "w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" })] }) }), _jsx("div", { className: "flex-1 overflow-y-auto", children: isLoading ? (_jsx("div", { className: "flex items-center justify-center h-40", children: _jsx(Loader, { className: "w-6 h-6 text-blue-500 animate-spin" }) })) : filteredItems.length === 0 ? (_jsx("div", { className: "flex items-center justify-center h-40 text-slate-500", children: _jsxs("p", { children: ["No ", activeTab, "s found"] }) })) : (_jsx("div", { className: "space-y-1 p-2", children: filteredItems.map(item => (_jsxs("div", { className: "flex items-center justify-between p-3 hover:bg-slate-50 rounded-lg transition-colors group", children: [_jsxs("div", { className: "flex-1 min-w-0", children: [_jsx("h4", { className: "font-medium text-slate-900 truncate", children: getItemName(item) }), _jsxs("div", { className: "text-xs text-slate-500 mt-1 space-y-0.5", children: [getItemDuration(item) !== '—' && (_jsxs("p", { children: ["Duration: ", getItemDuration(item)] })), getItemSubtitle(item) && (_jsxs("p", { children: ["Frequency: ", getItemSubtitle(item)] }))] })] }), _jsx("button", { onClick: () => handleAddItem(item.id), disabled: isAdding === item.id, className: "ml-2 px-3 py-1 bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white text-sm rounded transition-colors flex-shrink-0", children: isAdding === item.id ? (_jsx(Loader, { className: "w-4 h-4 animate-spin" })) : ('Add') })] }, item.id))) })) })] }) }));
}
